import { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'src/containers/withRouter/withRouter';
import { Navigate } from 'react-router-dom';
import { withTheme } from 'styled-components';
import { Helmet } from 'react-helmet';
import { LoginScreen, Button, Loader } from '@jjj/components';
import appActions from '../../redux/app/actions';
import authAction from '../../redux/auth/actions';
import signInActions from './email/actions';
import { steps } from '../../redux/auth/reducer';
import { withSSOTokenHandler } from './withSSOTokenHandler';
import Wrapper from './signIn.style';
import { formatOrganizationCode } from '../../helpers/formatters';
import { fakeSignInData, isDayforce, isReview, isDevOrReview } from '../../settings';
import { updateSettings, getSettings, getLastVisitedRoute, getDefaultInitialRoute } from '../../helpers/utility';
import { t } from '../../i18n';
import { COUNTRY_CONTEXT_KEY } from '../../helpers/localStorageKeys.const';
import DevelopmentFields from './developmentFields/developmentFields';
import notifications from '../../redux/notifications/actions';

class SignIn extends Component {
  loginTypes = {
    SIGN_IN_PASSWORD: 'SIGN_IN_PASSWORD',
    SIGN_IN_WITH_SSO: 'SIGN_IN_WITH_SSO',
    SIGN_IN_WITH_SSO_AND_PASSWORD: 'SIGN_IN_WITH_SSO_AND_PASSWORD',
    SIGN_IN_WITH_GOOGLE: 'SIGN_IN_WITH_GOOGLE',
  };

  LOGIN_METHODS = {
    password: 'password',
    sso: 'sso',
    google: 'google',
  };

  state = {
    apiUrl: getSettings().apiUrl,
    wsUrl: getSettings().wsUrl,
    organizationCode: getSettings().organizationCode,
  };

  componentDidMount() {
    const { match, location, auth, setStep, checkCreatePasswordToken, checkResetPasswordToken } = this.props;
    const step = this.determineStep(match.url.toLowerCase(), auth);

    if (!auth.isCheckingLoginMethods) {
      setStep({ step });
    }

    if (step === steps.CREATE_PASSWORD || step === steps.RESET_PASSWORD) {
      const token = location.search.replace('?token=', '');

      if (step === steps.CREATE_PASSWORD && token) {
        checkCreatePasswordToken({ token });
      }

      if (step === steps.RESET_PASSWORD && token) {
        checkResetPasswordToken({ token });
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps?.auth?.hasError !== this.props?.auth?.hasError && this.props?.auth?.hasError) {
      this.props.addNotification({
        message: t('dash.signIn.wrong_password_error'),
        headline: t('dash.common.notification.error_headline'),
        type: 'error',
      });
    }

    if (prevProps?.auth?.email?.error !== this.props?.auth?.email?.error && this.props?.auth?.email?.error) {
      this.props.addNotification({
        message: this.props?.auth?.email?.error,
        headline: t('dash.common.notification.error_headline'),
        type: 'error',
      });
    }

    if (prevProps?.auth?.step !== this.props?.auth?.step && this.props?.auth?.step === steps.START) {
      this.props.clearSignInStore();
    }
  }

  determineStep = (url, auth) => {
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get('token');

    switch (url) {
      case '/passwords/new':
        return steps.CREATE_PASSWORD;
      case '/passwords/forgot':
        return steps.RESET_PASSWORD;
      default: {
        const loginMethods = auth?.loginMethods;
        const includesSso = loginMethods?.includes(this.LOGIN_METHODS.sso);
        const shouldShowLoginStep = !loginMethods || !includesSso || (includesSso && token);

        return shouldShowLoginStep ? steps.LOGIN : steps.START;
      }
    }
  };

  onRequest = () => this.props.googleLoginRequest();

  onInputChange = value => {
    if (value.organizationCode) {
      localStorage.removeItem(COUNTRY_CONTEXT_KEY);
    }

    updateSettings({
      ...getSettings(),
      ...(value.organizationCode && { organizationCode: formatOrganizationCode(value.organizationCode) }),
      ...(value.apiUrl && { apiUrl: value.apiUrl }),
      ...(value.wsUrl && { wsUrl: value.apiUrl }),
    });

    this.setState(value);
  };

  onResponse = response => {
    const { code } = response;

    if (code) {
      this.props.login({ code, isGoogleLogin: true });
    } else {
      this.props.loginError();
    }
  };

  onFakeLogin = () => {
    const { auth } = this.props;
    const isGoogleLogin = auth.email?.value && auth?.navigation?.key === this.loginTypes.SIGN_IN_WITH_GOOGLE;
    const { apiUrl, wsUrl, organizationCode } = this.state;

    this.props.googleLoginRequest();

    updateSettings({ ...getSettings(), apiUrl, wsUrl, organizationCode, email: auth.email?.value });

    this.props.login({
      ...fakeSignInData,
      isGoogleLogin,
      username: auth.email?.value,
      fakeProfile: {
        ...fakeSignInData.profileObj,
        ...(isGoogleLogin && { email: auth.email?.value }),
      },
    });
  };

  onAction = data => {
    const {
      auth,
      createOrResetPasswordToken,
      createPassword,
      resetPassword,
      history,
      setStep,
      login,
      sendConfirmationEmail,
    } = this.props;

    switch (auth.step) {
      case steps.CREATE_PASSWORD_TOKEN_VALID:
        createPassword({
          token: createOrResetPasswordToken,
          password: data.password,
          password_confirmation: data.password_confirmation,
        });
        break;
      case steps.RESET_PASSWORD_TOKEN_VALID:
        resetPassword({
          token: createOrResetPasswordToken,
          password: data.password,
          password_confirmation: data.password_confirmation,
        });
        break;
      case steps.CREATE_PASSWORD_SUCCESS:
      case steps.RESET_PASSWORD_SUCCESS:
        this.props.clearSignInStore();
        history.push({ pathname: '/' });

        if (auth?.loginMethods?.length === 1) {
          setStep({ step: steps.LOGIN });
        } else {
          setStep({ step: steps.START });
        }
        break;
      case steps.RESET_PASSWORD:
        sendConfirmationEmail({ email: data.username });
        break;
      case steps.LOGIN:
        updateSettings({
          ...getSettings(),
          apiUrl: this.state.apiUrl,
          wsUrl: this.state.wsUrl,
          organizationCode: this.state.organizationCode,
        });

        login(data);
        break;
      default:
        return;
    }
  };

  renderBackToLoginBtn = () => {
    return (
      <Button type="link" className="back-to-login-link" onClick={this.backToLogin}>
        {t('dash.signIn.back_to_login_label')}
      </Button>
    );
  };

  backToLogin = () => {
    const { history, setStep, auth, clearSignInStore } = this.props;

    clearSignInStore();
    this.handleChangeProperty({ attr: 'email', value: '', touched: false, error: '', step: auth?.step });

    if (auth?.loginMethods?.length === 1) {
      setStep({ step: steps.LOGIN });
    } else {
      setStep({ step: steps.START });
    }
    history.push('/');
  };

  backToEmail = () => {
    const { auth, setStep, clearSignInStore } = this.props;
    clearSignInStore();

    this.handleChangeProperty({ attr: 'email', value: '', touched: false, error: '', step: auth?.step });
    setStep({ step: steps.START });
  };

  gotToResetPasswordPage = () => {
    const { history, setStep, clearSignInStore } = this.props;
    clearSignInStore();

    setStep({ step: steps.RESET_PASSWORD });
    history.push('/passwords/forgot');
  };

  renderInfo = ({ title, description }) => {
    return (
      <div className="login-information">
        <h3>{title}</h3>
        <h4>{description}</h4>
      </div>
    );
  };

  renderStepInformation = () => {
    let title;
    let description;
    switch (this.props.auth.step) {
      case steps.RESET_PASSWORD_EMAIL_SENT:
        title = t('dash.signIn.reset_password_title');
        description = t('dash.signIn.email_sent_description');
        break;
      case steps.RESET_PASSWORD_CONFIRMATION:
      case steps.RESET_PASSWORD_TOKEN_VALID:
        title = t('dash.signIn.reset_password_title');
        description = t('dash.signIn.confirm_new_password_description');
        break;
      case steps.RESET_PASSWORD_SUCCESS:
        title = t('dash.signIn.reset_password_success_title');
        description = t('dash.signIn.password_reset_description');
        break;
      case steps.CREATE_PASSWORD_TOKEN_EXPIRED:
        title = t('dash.signIn.create_password_expired_title');
        description = t('dash.signIn.create_link_expired_description');
        break;
      case steps.RESET_PASSWORD_TOKEN_EXPIRED:
        title = t('dash.signIn.reset_password_expired_title');
        description = t('dash.signIn.reset_link_expired_description');
        break;
      default:
        break;
    }

    return title || description ? { title, description } : null;
  };

  handleCheckLoginTypeButtonClick = () => {
    const { setFormSubmitted, touch } = this.props;
    const { email, step } = this.props.auth;
    setFormSubmitted({ form: 'emailForm', submitted: true });

    if (!email?.value || email?.error) {
      touch({ step });
    } else {
      this.props.checkLoginTypeRequest({ email: email.value });
    }
  };

  onKeyPress = event => {
    if (event.charCode === 13) {
      const { email } = this.props.auth;

      this.props.checkLoginTypeRequest({ email: email.value });
    }
  };

  handleChangeProperty = ({ attr, value, step, touched, error }) => {
    const { onChangeProperty, onSignInChangeProperty } = this.props;

    if (attr === 'email') {
      onChangeProperty({ attr, value, touched, error });

      if ([steps.START]?.includes(this.props?.auth.step)) {
        onSignInChangeProperty({ attr: 'firstInput', value, step, touched: false, error: '' });
      }
      updateSettings({
        ...getSettings(),
      });
    } else {
      onChangeProperty({ attr, value });
    }
  };

  hasErrors = () => {
    const { email } = this.props.auth;

    return !!email.error && email.touched;
  };

  handleResetPasswordFormSubmit = () => {
    const { signInData, auth, onSignInChangeProperty, touch, setFormSubmitted } = this.props;
    const { firstInput } = signInData;
    setFormSubmitted({ form: 'resetPasswordForm', submitted: true });

    if (!firstInput?.value || firstInput?.error) {
      touch({ step: auth?.step });
    } else {
      this.onAction({ username: firstInput.value });
      onSignInChangeProperty({ attr: 'firstInput', value: '', step: auth?.step, touched: false, error: '' });
    }
  };

  handlePasswordConfirmSubmit = () => {
    const { signInData, touch } = this.props;
    const { firstInput, secondInput } = signInData;

    if (firstInput.error || secondInput.error) {
      touch();
    } else {
      this.onAction({
        password: firstInput.value,
        password_confirmation: secondInput.value,
      });
    }
  };

  renderResetPasswordForm = (step, isLoading, showBackToLoginBtn, isCheckingToken) => {
    const { signInData, onSignInChangeProperty } = this.props;
    const { formSubmitted } = signInData;
    const { resetPasswordForm: isResetPasswordFormSubmitted } = formSubmitted;

    if (isCheckingToken) {
      return <Loader />;
    }

    if (step === steps.RESET_PASSWORD) {
      return (
        <LoginScreen
          variant="reset-password-email"
          email={signInData?.firstInput?.value || ''}
          onEmailChange={value => onSignInChangeProperty({ attr: 'firstInput', value, step })}
          onEmailSubmit={this.handleResetPasswordFormSubmit}
          emailError={
            isResetPasswordFormSubmitted && signInData?.firstInput?.touched ? signInData?.firstInput?.error : ''
          }
          emailTouched={signInData?.firstInput?.touched}
          emailLabel={t('dash.signIn.email_address_label')}
          emailPlaceholder={t('dash.signIn.input_username_placeholder')}
          loading={isLoading}
          dataCy="sign-in-reset-password-step"
          customFooter={showBackToLoginBtn ? this.renderBackToLoginBtn() : null}
        />
      );
    }

    if (step === steps.RESET_PASSWORD_EMAIL_SENT) {
      return (
        <LoginScreen
          variant="info-message"
          stepDetails={this.renderStepInformation()}
          dataCy="sign-in-reset-password-email-sent-step"
          customFooter={showBackToLoginBtn ? this.renderBackToLoginBtn() : null}
        />
      );
    }

    if (step === steps.RESET_PASSWORD_TOKEN_VALID || step === steps.RESET_PASSWORD_CONFIRMATION) {
      return (
        <LoginScreen
          variant="password-confirm"
          password={signInData?.firstInput?.value || ''}
          passwordConfirm={signInData?.secondInput?.value || ''}
          onPasswordChange={value => onSignInChangeProperty({ attr: 'firstInput', value, step })}
          onPasswordConfirmChange={value => onSignInChangeProperty({ attr: 'secondInput', value, step })}
          onSubmit={this.handlePasswordConfirmSubmit}
          passwordError={signInData?.firstInput?.touched ? signInData?.firstInput?.error : ''}
          passwordConfirmError={signInData?.secondInput?.touched ? signInData?.secondInput?.error : ''}
          passwordTouched={signInData?.firstInput?.touched}
          passwordConfirmTouched={signInData?.secondInput?.touched}
          passwordLabel={t('dash.signIn.new_password_label')}
          passwordConfirmLabel={t('dash.signIn.confirm_new_password_label')}
          passwordPlaceholder={t('dash.signIn.input_new_password_placeholder')}
          passwordConfirmPlaceholder={t('dash.signIn.confirm_new_password_placeholder')}
          submitButtonText={t('dash.signIn.reset_password_label')}
          loading={isLoading}
          dataCy="sign-in-reset-password-confirm-step"
          customFooter={showBackToLoginBtn ? this.renderBackToLoginBtn() : null}
        />
      );
    }

    if (step === steps.RESET_PASSWORD_SUCCESS) {
      return (
        <LoginScreen
          variant="info-message"
          stepDetails={this.renderStepInformation()}
          showButton
          buttonText={t('dash.signIn.back_to_login_label')}
          onButtonClick={() => this.onAction()}
          loading={isLoading}
          dataCy="sign-in-reset-password-success-step"
        />
      );
    }

    if (step === steps.RESET_PASSWORD_TOKEN_EXPIRED) {
      return (
        <LoginScreen
          variant="info-message"
          stepDetails={this.renderStepInformation()}
          dataCy="sign-in-reset-password-expired-step"
          customFooter={showBackToLoginBtn ? this.renderBackToLoginBtn() : null}
        />
      );
    }

    if (step === steps.CREATE_PASSWORD || step === steps.CREATE_PASSWORD_TOKEN_VALID) {
      return (
        <LoginScreen
          variant="password-confirm"
          password={signInData?.firstInput?.value || ''}
          passwordConfirm={signInData?.secondInput?.value || ''}
          onPasswordChange={value => onSignInChangeProperty({ attr: 'firstInput', value, step })}
          onPasswordConfirmChange={value => onSignInChangeProperty({ attr: 'secondInput', value, step })}
          onSubmit={this.handlePasswordConfirmSubmit}
          passwordError={signInData?.firstInput?.touched ? signInData?.firstInput?.error : ''}
          passwordConfirmError={signInData?.secondInput?.touched ? signInData?.secondInput?.error : ''}
          passwordTouched={signInData?.firstInput?.touched}
          passwordConfirmTouched={signInData?.secondInput?.touched}
          passwordLabel={t('dash.signIn.password_label')}
          passwordConfirmLabel={t('dash.signIn.confirm_password_label')}
          passwordPlaceholder={t('dash.signIn.input_password_placeholder')}
          passwordConfirmPlaceholder={t('dash.signIn.confirm_password_placeholder')}
          submitButtonText={t('dash.signIn.create_password_label')}
          loading={isLoading}
          dataCy="sign-in-create-password-step"
          customFooter={showBackToLoginBtn ? this.renderBackToLoginBtn() : null}
        />
      );
    }

    if (step === steps.CREATE_PASSWORD_SUCCESS) {
      return (
        <LoginScreen
          variant="info-message"
          stepDetails={this.renderStepInformation()}
          showButton
          buttonText={t('dash.signIn.back_to_login_label')}
          onButtonClick={() => this.onAction()}
          loading={isLoading}
          dataCy="sign-in-create-password-success-step"
        />
      );
    }

    if (step === steps.CREATE_PASSWORD_TOKEN_EXPIRED) {
      return (
        <LoginScreen
          variant="info-message"
          stepDetails={this.renderStepInformation()}
          dataCy="sign-in-create-password-expired-step"
          customFooter={showBackToLoginBtn ? this.renderBackToLoginBtn() : null}
        />
      );
    }

    return null;
  };

  render() {
    const { apiUrl, wsUrl, organizationCode } = this.state;
    const { auth, theme, loginWithSSOData, signInData, isUnifiedLoginEnabled } = this.props;
    const {
      email,
      isCheckingLoginType,
      isLoggedIn: isLoggedInFromAuth,
      loading,
      role,
      step,
      navigation,
      isCheckingResetPasswordToken,
      isCheckingCreatePasswordToken,
    } = auth;
    const sso = navigation?.params?.sso;
    const isLoggedIn = !!isLoggedInFromAuth;
    const { isLoggingInWithSSO } = loginWithSSOData;

    const isGoogleLogin =
      navigation?.key === this.loginTypes.SIGN_IN_WITH_GOOGLE || auth.loginMethods?.[0] === this.LOGIN_METHODS.google;
    const path = getLastVisitedRoute(role);
    const [, currentMenuItem] = path.split('?')[0].split('/');

    const showDevelopmentFields =
      (isDevOrReview && step === steps.START) ||
      (isDevOrReview && step === steps.LOGIN && auth.loginMethods?.length === 1);
    const isSSOOnly =
      step === steps.LOGIN &&
      !isCheckingLoginType &&
      navigation?.key !== this.loginTypes.SIGN_IN_PASSWORD &&
      navigation?.key !== this.loginTypes.SIGN_IN_WITH_GOOGLE &&
      navigation?.key !== this.loginTypes.SIGN_IN_WITH_SSO_AND_PASSWORD &&
      (navigation?.key === this.loginTypes.SIGN_IN_WITH_SSO || auth.loginMethods?.includes(this.LOGIN_METHODS.sso));
    const showLoginForm =
      !isCheckingLoginType &&
      !isLoggingInWithSSO &&
      !isGoogleLogin &&
      !isSSOOnly &&
      step === steps.LOGIN &&
      (navigation?.key === this.loginTypes.SIGN_IN_PASSWORD || this.loginTypes.SIGN_IN_WITH_SSO_AND_PASSWORD);
    const isSSOAndPasswordAvailable =
      step === steps.LOGIN &&
      !isCheckingLoginType &&
      auth.loginMethods?.length === 2 &&
      navigation?.key === this.loginTypes.SIGN_IN_WITH_SSO_AND_PASSWORD;
    const showResetPasswordForm = !isGoogleLogin && step !== steps.LOGIN && step !== steps.START;
    const showBackToLoginBtn = ![steps.LOGIN, steps.CREATE_PASSWORD_SUCCESS, steps.RESET_PASSWORD_SUCCESS]?.includes(
      step,
    );
    const from = { pathname: path.split('?')[0], search: path.split('?')[1] };

    const { formSubmitted } = signInData;
    const {
      emailPasswordForm: isEmailPasswordFormSubmitted,
      emailForm: isEmailFormSubmitted,
      passwordSSOForm: isPasswordSSOFormSubmitted,
    } = formSubmitted;

    this.props.changeCurrent(currentMenuItem);

    if (isLoggedIn) {
      if (from.pathname === '/' || from.pathname === '/404') {
        const defaultInitialRoute = getDefaultInitialRoute(role);

        return <Navigate to={defaultInitialRoute} />;
      }

      return <Navigate to={from} />;
    }

    if (isUnifiedLoginEnabled) {
      return null;
    }

    const title = isDayforce()
      ? (theme.product_name || 'Dash') + (theme.org_name ? ` - ${theme.org_name}` : '')
      : theme.org_name || 'Dash';

    const isLoading = loading;

    return (
      <Wrapper>
        <Helmet defer={false}>
          <title>{title}</title>
        </Helmet>
        {step === steps.START && (
          <LoginScreen
            variant="email"
            email={email.value}
            onEmailChange={value => this.handleChangeProperty({ attr: 'email', value })}
            onEmailSubmit={this.handleCheckLoginTypeButtonClick}
            emailError={
              isEmailFormSubmitted && this.props.signInData?.firstInput?.touched
                ? this.props.signInData?.firstInput?.error
                : ''
            }
            emailTouched={this.props.signInData?.firstInput?.touched}
            loading={isCheckingLoginType}
            dataCy="sign-in-email-step"
            showDevelopmentFields={showDevelopmentFields}
            developmentFields={
              <DevelopmentFields
                isGoogleLogin={isGoogleLogin}
                apiUrl={apiUrl}
                wsUrl={wsUrl}
                organizationCode={organizationCode}
                onInputChange={this.onInputChange}
                onFakeLogin={this.onFakeLogin}
                isReview={isReview}
              />
            }
          />
        )}
        {showLoginForm && !isSSOAndPasswordAvailable && (
          <LoginScreen
            variant="email-password"
            email={this.props.signInData?.firstInput?.value || ''}
            onEmailChange={value => this.props.onSignInChangeProperty({ attr: 'firstInput', value, step: auth?.step })}
            password={this.props.signInData?.secondInput?.value || ''}
            onPasswordChange={value =>
              this.props.onSignInChangeProperty({ attr: 'secondInput', value, step: auth?.step })
            }
            onEmailSubmit={() => {
              this.props.setFormSubmitted({ form: 'emailPasswordForm', submitted: true });

              if (!this.props.signInData?.firstInput?.value || !this.props.signInData?.secondInput?.value) {
                this.props.touch({ step });
              } else {
                this.onAction({
                  username: this.props.signInData?.firstInput?.value,
                  password: this.props.signInData?.secondInput?.value,
                });
              }
            }}
            emailError={
              isEmailPasswordFormSubmitted && this.props.signInData?.firstInput?.touched
                ? this.props.signInData?.firstInput?.error
                : ''
            }
            emailTouched={this.props.signInData?.firstInput?.touched}
            passwordError={
              isEmailPasswordFormSubmitted && this.props.signInData?.secondInput?.touched
                ? this.props.signInData?.secondInput?.error
                : ''
            }
            passwordTouched={this.props.signInData?.secondInput?.touched}
            emailPlaceholder={t('dash.signIn.input_username_placeholder')}
            passwordPlaceholder={t('dash.signIn.input_password_placeholder')}
            loading={isLoading}
            showForgotPassword
            onForgotPassword={this.gotToResetPasswordPage}
            dataCy="sign-in-login-step"
            showDevelopmentFields={showDevelopmentFields}
            developmentFields={
              <DevelopmentFields
                isGoogleLogin={isGoogleLogin}
                apiUrl={apiUrl}
                wsUrl={wsUrl}
                organizationCode={organizationCode}
                onInputChange={this.onInputChange}
                onFakeLogin={this.onFakeLogin}
                isReview={isReview}
              />
            }
          />
        )}
        {showResetPasswordForm &&
          this.renderResetPasswordForm(
            step,
            isLoading,
            showBackToLoginBtn,
            isCheckingResetPasswordToken || isCheckingCreatePasswordToken,
          )}
        {step === steps.LOGIN && isSSOOnly && (
          <LoginScreen
            variant="sso"
            ssoUrl={sso?.url || ''}
            ssoButtonText={t('dash.signIn.continue_with_sso_button_label')}
            loading={isLoggingInWithSSO}
            showUseDifferentEmail
            useDifferentEmailText={t('dash.signIn.use_different_email_label')}
            onUseDifferentEmailClick={this.backToEmail}
            dataCy="sign-in-sso-step"
            showDevelopmentFields={showDevelopmentFields}
            developmentFields={
              <DevelopmentFields
                isGoogleLogin={isGoogleLogin}
                apiUrl={apiUrl}
                wsUrl={wsUrl}
                organizationCode={organizationCode}
                onInputChange={this.onInputChange}
                onFakeLogin={this.onFakeLogin}
                isReview={isReview}
              />
            }
          />
        )}

        {isSSOAndPasswordAvailable && (
          <LoginScreen
            variant="password-with-sso"
            password={this.props.signInData?.secondInput?.value || ''}
            onPasswordChange={value =>
              this.props.onSignInChangeProperty({ attr: 'secondInput', value, step: auth?.step })
            }
            onPasswordSubmit={() => {
              this.props.setFormSubmitted({ form: 'passwordSSOForm', submitted: true });

              if (!this.props.signInData?.secondInput?.value) {
                this.props.touch({ step });
              } else {
                this.onAction({
                  username: this.props.signInData?.firstInput?.value,
                  password: this.props.signInData?.secondInput?.value,
                });
              }
            }}
            passwordError={
              isPasswordSSOFormSubmitted && this.props.signInData?.secondInput?.touched
                ? this.props.signInData?.secondInput?.error
                : ''
            }
            passwordTouched={this.props.signInData?.secondInput?.touched}
            passwordPlaceholder={t('dash.signIn.input_password_placeholder')}
            ssoUrl={sso?.url}
            loading={isLoading}
            ssoLoading={isLoggingInWithSSO}
            dataCy="sign-in-password-sso-step"
            showUseDifferentEmail
            useDifferentEmailText={t('dash.signIn.use_different_email_label')}
            onUseDifferentEmailClick={this.backToEmail}
          />
        )}
      </Wrapper>
    );
  }
}

export default connect(
  state => ({
    auth: state.Auth,
    signInData: state.SignIn,
    createOrResetPasswordToken: state.Auth.createOrResetPasswordToken,
    loginWithSSOData: state.LoginWithSSO,
    orgName: state.App?.theme?.org_name,
    isUnifiedLoginEnabled: state.FeatureFlags.flags?.unified_login_enabled,
  }),
  dispatch => ({
    googleLoginRequest: /* istanbul ignore next */ () => dispatch(authAction.googleLoginRequest()),
    login: /* istanbul ignore next */ data => dispatch(authAction.login(data)),
    loginError: /* istanbul ignore next */ data => dispatch(authAction.loginError(data)),
    changeCurrent: /* istanbul ignore next */ data => dispatch(appActions.changeCurrent(data)),
    setStep: /* istanbul ignore next */ data => dispatch(authAction.setStep(data)),
    checkCreatePasswordToken: /* istanbul ignore next */ data => dispatch(authAction.checkCreatePasswordToken(data)),
    checkResetPasswordToken: /* istanbul ignore next */ data => dispatch(authAction.checkResetPasswordToken(data)),
    createPassword: /* istanbul ignore next */ data => dispatch(authAction.createPassword(data)),
    resetPassword: /* istanbul ignore next */ data => dispatch(authAction.resetPassword(data)),
    sendConfirmationEmail: /* istanbul ignore next */ data => dispatch(authAction.sendConfirmationEmail(data)),
    checkLoginTypeRequest: /* istanbul ignore next */ data => dispatch(authAction.checkLoginTypeRequest(data)),
    onChangeProperty: /* istanbul ignore next */ data => dispatch(authAction.changeProperty(data)),
    onSignInChangeProperty: /* istanbul ignore next */ data => dispatch(signInActions.changeProperty(data)),
    clearSignInStore: /* istanbul ignore next */ data => dispatch(signInActions.clearStore(data)),
    addNotification: /* istanbul ignore next */ data => dispatch(notifications.addNotification(data)),
    touch: /* istanbul ignore next */ data => dispatch(signInActions.touch(data)),
    setFormSubmitted: /* istanbul ignore next */ data => dispatch(signInActions.setFormSubmitted(data)),
  }),
)(withSSOTokenHandler(withRouter(withTheme(SignIn))));
